import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { Ban, Link as LinkIcon, Unlink, FileText, RefreshCw, X, FileCheck, Pencil, Trash2 } from 'lucide-react';
import { Table, type TableColumn } from '@/components/ui/Table';
import { VStack, HStack } from '@/components/ui/Stack';
import { apiFetch } from '@/lib/api';
import { Badge } from '@/components/ui/Badge/Badge';
import { SimpleTooltip } from '@/components/ui/Tooltip';
import { Button, TableActions, TableActionButton } from '@/components/ui';
import { VenueCard } from '../Step1Research/VenueCard';
import { ProposalDetailsModal } from './ProposalDetailsModal';
import { ManualProposalModal, type ManualProposalData } from './ManualProposalModal';
import { CventManualConnectModal, type CventSupplierOption } from './CventManualConnectModal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog/ConfirmDialog';
import { NoteIcon } from '../../NoteIcon';
import { CventService } from '@/services/CventService';
import { ResearchService } from '@/services/ResearchService';
import { CventIntegrationService } from '@/services/CventIntegrationService';
import { useNotificationStore } from '@/stores/notificationStore';
import type { VenueDetails, ResearchDestination, ShortlistWithVenue } from '@/services/ResearchService';
import { StarRating } from '@/components/ui/StarRating';

interface VenueWithDestination {
  venue: VenueDetails;
  destination: ResearchDestination;
  shortlistItem: ShortlistWithVenue;
}

interface ProposalInfo {
  status?: string;
  lowestRoomRate?: number;
  totalCost?: number;
  currencyCode?: string;
  hasProposal: boolean;
}

function isDeclinedProposalStatus(status?: string | null): boolean {
  if (!status) return false;
  const statusLower = status.toLowerCase();
  return (
    statusLower.includes('declined') ||
    statusLower === 'rejected' ||
    statusLower.includes('turned down') ||
    statusLower === 'turned_down'
  );
}

interface RFPVenuesTableProps {
  venuesWithDestination: VenueWithDestination[];
  onViewDetails: (venue: VenueDetails) => void;
  onUpdateNotes?: (shortlistId: string, notes: string, clientFacing: boolean) => void | Promise<void>;
  cventRfpId?: string | null;
  eventId?: string;
  onVenueDisconnected?: () => void; // Callback when venue is disconnected
  onManualProposalMarked?: () => void; // Callback when manual proposal is marked
  onVenueRemovedFromShortlist?: () => void; // Callback when venue is removed from shortlist
  onVenueManuallyConnected?: () => void; // Callback when manual Cvent connection is made
  cventVenueMap?: Map<string, string>; // Map of cvent_supplier_id -> venue name
  /** All Cvent suppliers on this RFP — drives the manual-connect modal. */
  cventSuppliers?: CventSupplierOption[];
}

export function RFPVenuesTable({
  venuesWithDestination,
  onViewDetails,
  onUpdateNotes,
  cventRfpId,
  eventId,
  onVenueDisconnected,
  onManualProposalMarked,
  onVenueRemovedFromShortlist,
  onVenueManuallyConnected,
  cventVenueMap,
  cventSuppliers,
}: RFPVenuesTableProps) {
  const [proposalData, setProposalData] = useState<Map<string, ProposalInfo>>(new Map());
  const [loadingProposals, setLoadingProposals] = useState<Set<string>>(new Set());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedProposal, setSelectedProposal] = useState<{ rfpId: string; supplierId: string; venueName: string; shortlistId: string } | null>(null);
  const [disconnectingIds, setDisconnectingIds] = useState<Set<string>>(new Set());
  const [unmarkingIds, setUnmarkingIds] = useState<Set<string>>(new Set());
  const [removingIds, setRemovingIds] = useState<Set<string>>(new Set());
  const [disconnectConfirm, setDisconnectConfirm] = useState<{ shortlistId: string; venueName: string } | null>(null);
  const fetchedSuppliersRef = useRef<Set<string>>(new Set());
  const loadingSuppliersRef = useRef<Set<string>>(new Set());

  // Manual proposal modal state
  const [manualProposalModal, setManualProposalModal] = useState<{
    shortlistId: string;
    venueName: string;
    mode: 'received' | 'declined';
    initialData?: ManualProposalData | null;
  } | null>(null);
  const [isSubmittingManualProposal, setIsSubmittingManualProposal] = useState(false);
  const [isDecliningManualProposal, setIsDecliningManualProposal] = useState(false);

  // Manual Cvent-connect modal state
  const [manualConnectFor, setManualConnectFor] = useState<{
    shortlistId: string;
    venueName: string;
  } | null>(null);

  // Build a "supplier id → name of the shortlist row already pointing at it"
  // map so the modal can warn the user about conflicts.
  const supplierToConnectedShortlistName = useMemo(() => {
    const map = new Map<string, string>();
    venuesWithDestination.forEach(({ shortlistItem, venue }) => {
      if (
        shortlistItem.cvent_supplier_id &&
        // exclude the row the user is currently fixing
        shortlistItem.id !== manualConnectFor?.shortlistId
      ) {
        map.set(shortlistItem.cvent_supplier_id, venue.property_name);
      }
    });
    return map;
  }, [venuesWithDestination, manualConnectFor?.shortlistId]);
  
  const cventService = new CventService();
  const researchService = new ResearchService();
  const cventIntegrationService = new CventIntegrationService();
  const notificationStore = useNotificationStore();

  // Load saved proposal data from shortlist items on mount
  // Merge with existing proposalData so live-fetched data isn't lost on re-renders
  useEffect(() => {
    setProposalData(prev => {
      const merged = new Map(prev);

      venuesWithDestination.forEach(({ shortlistItem }) => {
        if (shortlistItem.cvent_supplier_id && shortlistItem.cvent_proposal_status) {
          // Only set from DB data if we don't already have live-fetched data for this supplier
          if (!merged.has(shortlistItem.cvent_supplier_id)) {
            merged.set(shortlistItem.cvent_supplier_id, {
              status: shortlistItem.cvent_proposal_status,
              lowestRoomRate: shortlistItem.cvent_lowest_room_rate ?? undefined,
              totalCost: shortlistItem.cvent_total_cost ?? undefined,
              currencyCode: shortlistItem.cvent_currency_code || 'USD',
              hasProposal: !!shortlistItem.cvent_proposal_status && (
                shortlistItem.cvent_proposal_status.toLowerCase().includes('submitted') ||
                shortlistItem.cvent_proposal_status.toLowerCase().includes('received') ||
                shortlistItem.cvent_proposal_status === 'SUBMITTED_PROPOSAL'
              ),
            });
          }
        }
      });

      return merged;
    });
  }, [venuesWithDestination]);

  // Reset fetched suppliers when RFP ID changes
  useEffect(() => {
    fetchedSuppliersRef.current.clear();
    loadingSuppliersRef.current.clear();
    setLoadingProposals(new Set());
  }, [cventRfpId]);

  // Function to fetch proposals with delay between calls
  // Only refreshes proposals that are AWAITING_PROPOSAL or don't have proposal data
  async function fetchProposals() {
    if (!cventRfpId) return;

    // Filter to only venues that need refreshing:
    // 1. Don't have proposal status (no data yet)
    // 2. Have AWAITING_PROPOSAL status
    const connectedVenues = venuesWithDestination.filter(
      v => {
        if (!v.shortlistItem.cvent_supplier_id) return false;
        const currentStatus = v.shortlistItem.cvent_proposal_status;
        // Refresh if no status or if status is AWAITING_PROPOSAL
        return !currentStatus || currentStatus === 'AWAITING_PROPOSAL';
      }
    );

    if (connectedVenues.length === 0) return;

    setIsRefreshing(true);

    for (let i = 0; i < connectedVenues.length; i++) {
      const venue = connectedVenues[i];
      const supplierId = venue.shortlistItem.cvent_supplier_id;
      const shortlistId = venue.shortlistItem.id;
      if (!supplierId || !shortlistId) continue;
      
      // Check if currently loading
      if (loadingSuppliersRef.current.has(supplierId)) continue;
      
      loadingSuppliersRef.current.add(supplierId);
      setLoadingProposals(prev => new Set(prev).add(supplierId));

      try {
        const data = await cventService.fetchSupplierProposal(cventRfpId, supplierId) as any;
        
        // Fix: Use responseOverview.status (not responseStatus) based on actual API response
        const status = data?.responseOverview?.status || data?.status || data?.responseStatus;
        const currencyCode = data?.proposal?.currencyCode || 'USD';
        
        // Extract lowest room rate from proposalDerivedDetail or other locations
        const lowestRoomRate = data?.proposal?.proposalDerivedDetail?.lowestRoomRate ||
                              data?.responseOverview?.lowestSleepingRoomRate || 
                              data?.proposal?.sleepingRooms?.[0]?.lowestRate ||
                              data?.proposal?.sleepingRooms?.[0]?.lowestAverageDailyRate;
        
        // Calculate total cost from proposalDerivedDetail or aggregatedCosts
        let totalCost: number | undefined;
        if (data?.proposal?.proposalDerivedDetail) {
          const derived = data.proposal.proposalDerivedDetail;
          const sum = (derived.totalSleepRoomCost || 0) +
                     (derived.totalFnbCost || 0) +
                     (derived.totalMiscCost || 0);
          totalCost = sum > 0 ? sum : undefined;
        } else if (data?.proposal?.aggregatedCosts) {
          const costs = data.proposal.aggregatedCosts;
          totalCost = (costs.sleepingRoomsCost?.lowestTotalSleepingRoomCost || 0) +
                     (costs.totalFoodAndBeverageCost || 0) +
                     (costs.totalMiscCost || 0) +
                     (costs.packagesCost?.lowestTotalPackageCost || 0);
          if (totalCost === 0) totalCost = undefined;
        }

        // Extract response dates and convert to datetime format if needed
        // Cvent API returns dates as "YYYY-MM-DD" strings, convert to ISO datetime
        let originalResponseDate: string | null = null;
        let lastResponseDate: string | null = null;
        
        if (data?.responseOverview?.originalResponseDate) {
          const dateStr = data.responseOverview.originalResponseDate;
          // If it's just a date (YYYY-MM-DD), convert to datetime at midnight UTC
          if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
            originalResponseDate = `${dateStr}T00:00:00.000Z`;
          } else {
            // Already a datetime string, use as-is
            originalResponseDate = dateStr;
          }
        }
        
        if (data?.responseOverview?.lastResponseDate) {
          const dateStr = data.responseOverview.lastResponseDate;
          // If it's just a date (YYYY-MM-DD), convert to datetime at midnight UTC
          if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
            lastResponseDate = `${dateStr}T00:00:00.000Z`;
          } else {
            // Already a datetime string, use as-is
            lastResponseDate = dateStr;
          }
        }

        // Update UI state
        setProposalData(prev => {
          const newMap = new Map(prev);
          newMap.set(supplierId, {
            status,
            lowestRoomRate,
            totalCost,
            currencyCode,
            hasProposal: !!status && (
              status.toLowerCase().includes('submitted') || 
              status.toLowerCase().includes('received') ||
              status === 'SUBMITTED_PROPOSAL'
            ),
          });
          return newMap;
        });

        // Save full proposal data to database (stores all fields)
        try {
          // Store complete API response using the new endpoint
          await apiFetch(`/api/shortlist/${shortlistId}/store-proposal-data`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ proposal_data: data }),
          });
        } catch (saveError) {
          console.error(`Failed to save full proposal data for shortlist ${shortlistId}:`, saveError);
          // Fallback: Try to save at least the basic fields
          try {
            await researchService.updateShortlistItem(shortlistId, {
              cvent_proposal_status: status || null,
              cvent_lowest_room_rate: lowestRoomRate ?? null,
              cvent_total_cost: totalCost ?? null,
              cvent_currency_code: currencyCode || null,
              cvent_original_response_date: originalResponseDate,
              cvent_last_response_date: lastResponseDate,
              cvent_proposal_data_refreshed_at: new Date().toISOString(),
            });
          } catch (fallbackError) {
            console.error(`Failed to save fallback proposal data:`, fallbackError);
          }
        }

        fetchedSuppliersRef.current.add(supplierId);
      } catch (err) {
        console.error(`Failed to fetch proposal for supplier ${supplierId}:`, err);
        // Set empty proposal info on error
        setProposalData(prev => {
          const newMap = new Map(prev);
          newMap.set(supplierId, {
            hasProposal: false,
          });
          return newMap;
        });
        fetchedSuppliersRef.current.add(supplierId);
      } finally {
        loadingSuppliersRef.current.delete(supplierId);
        setLoadingProposals(prev => {
          const newSet = new Set(prev);
          newSet.delete(supplierId);
          return newSet;
        });
      }

      // Add delay between requests (except for the last one) to avoid rate limiting
      if (i < connectedVenues.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 500)); // 500ms delay
      }
    }

    setIsRefreshing(false);
  }

  function formatCurrency(amount: number | undefined, currencyCode: string = 'USD'): string {
    if (amount === undefined || amount === null) return '-';
    
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode || 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount);
    } catch {
      return `${currencyCode || 'USD'} ${amount.toLocaleString()}`;
    }
  }

  // Handle disconnecting a venue from Cvent
  const handleDisconnectVenue = useCallback((shortlistId: string, venueName: string) => {
    setDisconnectConfirm({ shortlistId, venueName });
  }, []);

  const handleConfirmDisconnect = useCallback(async () => {
    if (!eventId || !disconnectConfirm) return;
    const { shortlistId, venueName } = disconnectConfirm;
    setDisconnectConfirm(null);

    setDisconnectingIds(prev => new Set(prev).add(shortlistId));

    try {
      await cventIntegrationService.disconnectVenueFromCvent({
        eventId,
        shortlistId,
      });

      notificationStore.success(`Disconnected ${venueName} from Cvent`, {
        title: 'Disconnected',
      });

      // Notify parent to refresh data (this will retrigger matching)
      onVenueDisconnected?.();
    } catch (error) {
      console.error('Failed to disconnect venue:', error);
      notificationStore.error('Failed to disconnect venue from Cvent', {
        title: 'Error',
      });
    } finally {
      setDisconnectingIds(prev => {
        const next = new Set(prev);
        next.delete(shortlistId);
        return next;
      });
    }
  }, [eventId, disconnectConfirm, cventIntegrationService, notificationStore, onVenueDisconnected]);

  // Handle marking a venue as having a manually submitted proposal
  const handleMarkManualProposal = useCallback(async (data: ManualProposalData) => {
    if (!manualProposalModal) return;

    setIsSubmittingManualProposal(true);

    try {
      await researchService.markManualProposal(manualProposalModal.shortlistId, data);

      notificationStore.success(`Marked ${manualProposalModal.venueName} as proposal received`, {
        title: 'Proposal Marked',
      });

      setManualProposalModal(null);
      onManualProposalMarked?.();
    } catch (error) {
      console.error('Failed to mark manual proposal:', error);
      notificationStore.error('Failed to mark proposal as received', {
        title: 'Error',
      });
    } finally {
      setIsSubmittingManualProposal(false);
    }
  }, [manualProposalModal, researchService, notificationStore, onManualProposalMarked]);

  // Handle marking a venue as manually declined outside of Cvent
  const handleMarkManualDeclined = useCallback(async (data: ManualProposalData) => {
    if (!manualProposalModal) return;

    setIsDecliningManualProposal(true);

    try {
      await researchService.markManualDeclined(manualProposalModal.shortlistId, {
        notes: data.notes,
      });

      notificationStore.success(`Marked ${manualProposalModal.venueName} as declined`, {
        title: 'Proposal Declined',
      });

      setManualProposalModal(null);
      onManualProposalMarked?.();
    } catch (error) {
      console.error('Failed to mark manual decline:', error);
      notificationStore.error('Failed to mark proposal as declined', {
        title: 'Error',
      });
    } finally {
      setIsDecliningManualProposal(false);
    }
  }, [manualProposalModal, researchService, notificationStore, onManualProposalMarked]);

  // Handle unmarking a venue from manual proposal (undo manual marking, allows reconnecting to Cvent)
  const handleUnmarkManualProposal = useCallback(async (shortlistId: string, venueName: string) => {
    setUnmarkingIds(prev => new Set(prev).add(shortlistId));
    try {
      await researchService.unmarkManualProposal(shortlistId);
      notificationStore.success(`Removed manual marking for ${venueName}. You can reconnect to Cvent.`, {
        title: 'Manual Marking Removed',
      });
      onManualProposalMarked?.();
    } catch (error) {
      console.error('Failed to unmark manual proposal:', error);
      notificationStore.error('Failed to remove manual proposal marking', {
        title: 'Error',
      });
    } finally {
      setUnmarkingIds(prev => {
        const next = new Set(prev);
        next.delete(shortlistId);
        return next;
      });
    }
  }, [researchService, notificationStore, onManualProposalMarked]);

  const handleRemoveFromShortlist = useCallback(async (shortlistId: string, venueName: string) => {
    setRemovingIds(prev => new Set(prev).add(shortlistId));
    try {
      await researchService.removeFromShortlist(shortlistId);
      notificationStore.success(`${venueName} removed from shortlist`, {
        title: 'Removed',
      });
      onVenueRemovedFromShortlist?.();
    } catch (error) {
      console.error('Failed to remove from shortlist:', error);
      notificationStore.error('Failed to remove venue from shortlist', {
        title: 'Error',
      });
    } finally {
      setRemovingIds(prev => {
        const next = new Set(prev);
        next.delete(shortlistId);
        return next;
      });
    }
  }, [researchService, notificationStore, onVenueRemovedFromShortlist]);

  function getStatusBadge(status?: string, isManual?: boolean) {
    if (!status) return <span className="text-muted-foreground">-</span>;

    const statusLower = status.toLowerCase();
    let variant: 'success' | 'warning' | 'error' | 'secondary' = 'secondary';

    if (statusLower.includes('received') || statusLower.includes('submitted') || statusLower === 'accepted') {
      variant = 'success';
    } else if (statusLower.includes('pending') || statusLower.includes('draft')) {
      variant = 'warning';
    } else if (statusLower.includes('declined') || statusLower === 'rejected' || statusLower.includes('turned down') || statusLower === 'turned_down') {
      variant = 'error';
    }

    return (
      <HStack gap={1}>
        <Badge variant={variant} className="text-xs">
          {status}
        </Badge>
        {isManual && (
          <SimpleTooltip content="Manually recorded (not via Cvent)">
            <Badge variant="secondary" className="text-xs">
              Manual
            </Badge>
          </SimpleTooltip>
        )}
      </HStack>
    );
  }

  const columns = useMemo<TableColumn<VenueWithDestination>[]>(() => [
    {
      key: 'property_name',
      title: 'Property Name',
      sortable: true,
      render: (_value: any, record: VenueWithDestination) => (
        <div>
          <button
            onClick={() => onViewDetails(record.venue)}
            className="font-medium text-secondary-600 hover:text-secondary-700 hover:underline text-left"
          >
            {record.venue.property_name}
          </button>
          {/* Show city/country subtitle */}
          {record.venue.city && (
            <div className="text-sm text-gray-500">
              {record.venue.city}{record.venue.country ? `, ${record.venue.country}` : ''}
            </div>
          )}
        </div>
      )
    },
    {
      key: 'cvent',
      title: 'Cvent',
      sortable: false,
      render: (_value: any, record: VenueWithDestination) => {
        const isConnected = !!record.shortlistItem.cvent_supplier_id;
        const cventId = record.shortlistItem.cvent_supplier_id;
        const isDisconnecting = disconnectingIds.has(record.shortlistItem.id);
        
        // Get Cvent venue name if available
        const cventVenueName = cventId && cventVenueMap?.get(cventId);
        const tooltipContent = cventVenueName 
          ? (
            <div className="text-left">
              <div>{cventVenueName}</div>
              <div className="text-xs text-gray-400 mt-0.5">Cvent ID: {cventId}</div>
            </div>
          )
          : cventId 
            ? `Cvent ID: ${cventId}`
            : 'Not connected to Cvent';
        
        return (
          <div className="flex items-center gap-2">
            {isConnected ? (
              <>
                <SimpleTooltip
                  content={tooltipContent}
                  side="top"
                >
                  <Badge variant="success" className="text-xs">
                    Connected
                  </Badge>
                </SimpleTooltip>
                <SimpleTooltip
                  content="Disconnect from Cvent"
                  side="top"
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDisconnectVenue(record.shortlistItem.id, record.venue.property_name);
                    }}
                    disabled={isDisconnecting}
                    className={`
                      p-1 rounded hover:bg-error-50 transition-colors
                      ${isDisconnecting ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                    `}
                    aria-label="Disconnect from Cvent"
                  >
                    <X className="w-3.5 h-3.5 text-error-600 hover:text-error-700" />
                  </button>
                </SimpleTooltip>
              </>
            ) : (
              cventSuppliers && cventSuppliers.length > 0 ? (
                <SimpleTooltip
                  content="Click to manually connect to a Cvent supplier"
                  side="top"
                >
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setManualConnectFor({
                        shortlistId: record.shortlistItem.id,
                        venueName: record.venue.property_name,
                      });
                    }}
                    className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-md border border-warning-300 bg-warning-50 text-warning-800 hover:bg-warning-100 transition-colors"
                  >
                    Not Connected
                    <LinkIcon className="w-3 h-3" />
                  </button>
                </SimpleTooltip>
              ) : (
                <SimpleTooltip
                  content="Not connected to Cvent"
                  side="top"
                >
                  <Badge variant="secondary" className="text-xs">
                    Not Connected
                  </Badge>
                </SimpleTooltip>
              )
            )}
          </div>
        );
      }
    },
    {
      key: 'number_of_rooms',
      title: 'Rooms',
      sortable: true,
      render: (_value: any, record: VenueWithDestination) => 
        record.venue.number_of_rooms?.toLocaleString() || '-'
    },
    {
      key: 'maximum_capacity',
      title: 'Max Capacity',
      sortable: true,
      render: (_value: any, record: VenueWithDestination) => 
        record.venue.maximum_capacity?.toLocaleString() || '-'
    },
    {
      key: 'star_rating',
      title: 'Stars',
      sortable: true,
      render: (_value: any, record: VenueWithDestination) =>
        record.venue.star_rating ? <StarRating rating={record.venue.star_rating} size="sm" showEmptyStars={true} /> : '-'
    },
    ...(cventRfpId ? [
      {
        key: 'proposal_status',
        title: 'Proposal Status',
        sortable: false,
        render: (_value: any, record: VenueWithDestination) => {
          // Check for manual proposal first
          if (record.shortlistItem.is_manual_proposal && record.shortlistItem.cvent_proposal_status) {
            return getStatusBadge(record.shortlistItem.cvent_proposal_status, true);
          }

          if (!record.shortlistItem.cvent_supplier_id) return <span className="text-muted-foreground">-</span>;

          const supplierId = record.shortlistItem.cvent_supplier_id;
          const proposal = proposalData.get(supplierId);

          if (loadingProposals.has(supplierId)) {
            return <span className="text-xs text-muted-foreground">Loading...</span>;
          }

          return proposal ? getStatusBadge(proposal.status) : <span className="text-muted-foreground">-</span>;
        }
      } as TableColumn<VenueWithDestination>,
      {
        key: 'lowest_room_rate',
        title: 'Lowest Room Rate',
        sortable: false,
        render: (_value: any, record: VenueWithDestination) => {
          // For manual proposals, use the shortlistItem data directly
          if (record.shortlistItem.is_manual_proposal) {
            const rate = record.shortlistItem.cvent_lowest_room_rate;
            if (rate === null || rate === undefined) {
              return <span className="text-muted-foreground">-</span>;
            }
            return (
              <span className="text-sm font-medium">
                {formatCurrency(rate, record.shortlistItem.cvent_currency_code || 'USD')}
              </span>
            );
          }

          // For Cvent proposals, use the proposalData map
          if (!record.shortlistItem.cvent_supplier_id) return <span className="text-muted-foreground">-</span>;

          const supplierId = record.shortlistItem.cvent_supplier_id;
          const proposal = proposalData.get(supplierId);

          if (!proposal || proposal.lowestRoomRate === undefined) {
            return <span className="text-muted-foreground">-</span>;
          }

          return (
            <span className="text-sm font-medium">
              {formatCurrency(proposal.lowestRoomRate, proposal.currencyCode)}
            </span>
          );
        }
      } as TableColumn<VenueWithDestination>,
      {
        key: 'total_cost',
        title: 'Total Cost',
        sortable: false,
        render: (_value: any, record: VenueWithDestination) => {
          // For manual proposals, use the shortlistItem data directly
          if (record.shortlistItem.is_manual_proposal) {
            const cost = record.shortlistItem.cvent_total_cost;
            if (cost === null || cost === undefined) {
              return <span className="text-muted-foreground">-</span>;
            }
            return (
              <span className="text-sm font-medium">
                {formatCurrency(cost, record.shortlistItem.cvent_currency_code || 'USD')}
              </span>
            );
          }

          // For Cvent proposals, use the proposalData map
          if (!record.shortlistItem.cvent_supplier_id) return <span className="text-muted-foreground">-</span>;

          const supplierId = record.shortlistItem.cvent_supplier_id;
          const proposal = proposalData.get(supplierId);

          if (!proposal || proposal.totalCost === undefined) {
            return <span className="text-muted-foreground">-</span>;
          }

          return (
            <span className="text-sm font-medium">
              {formatCurrency(proposal.totalCost, proposal.currencyCode)}
            </span>
          );
        }
      } as TableColumn<VenueWithDestination>,
      ...(onUpdateNotes ? [{
        key: 'notes',
        title: 'Notes',
        render: (_value: any, record: VenueWithDestination) => (
          <NoteIcon
            notes={record.shortlistItem.notes}
            clientFacing={record.shortlistItem.notes_client_facing}
            venueName={record.venue.property_name}
            onUpdateNotes={(notes, clientFacing) => onUpdateNotes(record.shortlistItem.id, notes, clientFacing)}
          />
        )
      } as TableColumn<VenueWithDestination>] : []),
      {
        key: 'proposal_action',
        title: 'Actions',
        sortable: false,
        render: (_value: any, record: VenueWithDestination) => {
          const hasSubmittedProposal = record.shortlistItem.cvent_proposal_status === 'SUBMITTED_PROPOSAL';
          const isDeclinedProposal = isDeclinedProposalStatus(record.shortlistItem.cvent_proposal_status);
          const isConnectedToCvent = !!record.shortlistItem.cvent_supplier_id;
          const supplierId = record.shortlistItem.cvent_supplier_id;
          const proposal = supplierId ? proposalData.get(supplierId) : null;
          const hasCventProposal = proposal?.hasProposal;

          return (
            <TableActions>
              {/* View Proposal - show for Cvent proposals */}
              {isConnectedToCvent && hasCventProposal && cventRfpId && (
                <TableActionButton
                  icon={FileText}
                  onClick={() => setSelectedProposal({
                    rfpId: cventRfpId,
                    supplierId: supplierId!,
                    venueName: record.venue.property_name,
                    shortlistId: record.shortlistItem.id,
                  })}
                >
                  View
                </TableActionButton>
              )}
              {/* Mark as Received - show for venues without SUBMITTED_PROPOSAL status */}
              {!hasSubmittedProposal && (
                <SimpleTooltip content="Mark this venue as having submitted a proposal (e.g., via email)">
                  <TableActionButton
                    icon={FileCheck}
                    onClick={() => setManualProposalModal({
                      shortlistId: record.shortlistItem.id,
                      venueName: record.venue.property_name,
                      mode: 'received',
                      initialData: null,
                    })}
                  >
                    Mark Received
                  </TableActionButton>
                </SimpleTooltip>
              )}
              {/* Mark Declined - direct action for venues that declined outside Cvent */}
              {!hasSubmittedProposal && !isDeclinedProposal && (
                <SimpleTooltip content="Mark this venue as declined (e.g., via email outside Cvent)">
                  <TableActionButton
                    variant="destructive"
                    icon={Ban}
                    onClick={() => setManualProposalModal({
                      shortlistId: record.shortlistItem.id,
                      venueName: record.venue.property_name,
                      mode: 'declined',
                      initialData: null,
                    })}
                  >
                    Mark Declined
                  </TableActionButton>
                </SimpleTooltip>
              )}
              {/* Edit - show for manual proposals */}
              {record.shortlistItem.is_manual_proposal && hasSubmittedProposal && (
                <SimpleTooltip content="Edit manual proposal details">
                  <TableActionButton
                    icon={Pencil}
                    onClick={() => setManualProposalModal({
                      shortlistId: record.shortlistItem.id,
                      venueName: record.venue.property_name,
                      mode: 'received',
                      initialData: {
                        lowestRoomRate: record.shortlistItem.cvent_lowest_room_rate,
                        totalCost: record.shortlistItem.cvent_total_cost,
                        foodBeverageMinimum: record.shortlistItem.cvent_food_beverage_minimum,
                        currencyCode: record.shortlistItem.cvent_currency_code || 'USD',
                        notes: record.shortlistItem.notes || '',
                        checkInDate: record.shortlistItem.proposal_check_in_date,
                        checkOutDate: record.shortlistItem.proposal_check_out_date,
                      },
                    })}
                  >
                    Edit
                  </TableActionButton>
                </SimpleTooltip>
              )}
              {/* Remove Manual - show for manual proposals, allows reconnecting to Cvent */}
              {record.shortlistItem.is_manual_proposal && (
                <SimpleTooltip content="Remove manual marking and allow reconnecting to Cvent">
                  <TableActionButton
                    icon={Unlink}
                    onClick={() => handleUnmarkManualProposal(record.shortlistItem.id, record.venue.property_name)}
                    disabled={unmarkingIds.has(record.shortlistItem.id)}
                  >
                    Remove Manual
                  </TableActionButton>
                </SimpleTooltip>
              )}
              {/* Remove from Shortlist - only for venues NOT connected to Cvent */}
              {!isConnectedToCvent && (
                <SimpleTooltip content="Remove this venue from your shortlist">
                  <TableActionButton
                    icon={Trash2}
                    onClick={() => handleRemoveFromShortlist(record.shortlistItem.id, record.venue.property_name)}
                    disabled={removingIds.has(record.shortlistItem.id)}
                  >
                    Remove
                  </TableActionButton>
                </SimpleTooltip>
              )}
            </TableActions>
          );
        }
      } as TableColumn<VenueWithDestination>,
    ] : []),
  ], [onViewDetails, onUpdateNotes, cventRfpId, proposalData, loadingProposals, setSelectedProposal, disconnectingIds, unmarkingIds, removingIds, eventId, handleDisconnectVenue, handleUnmarkManualProposal, handleRemoveFromShortlist, setManualProposalModal]);

  const connectedVenuesCount = venuesWithDestination.filter(v => v.shortlistItem.cvent_supplier_id).length;
  const hasConnectedVenues = connectedVenuesCount > 0;

  return (
    <div>
      {/* Refresh Button - Only show if RFP is connected and there are connected venues */}
      {cventRfpId && hasConnectedVenues && (
        <div className="mb-4 flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            {isRefreshing ? (
              <span>Refreshing proposals... ({loadingProposals.size} of {connectedVenuesCount})</span>
            ) : (
              <span>{venuesWithDestination.length} shortlisted {venuesWithDestination.length === 1 ? 'venue' : 'venues'}, {connectedVenuesCount} connected to Cvent</span>
            )}
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={fetchProposals}
            disabled={isRefreshing}
            isLoading={isRefreshing}
            leftIcon={<RefreshCw className="w-4 h-4" />}
          >
            {isRefreshing ? 'Refreshing...' : 'Refresh Proposals'}
          </Button>
        </div>
      )}

      {/* Mobile: Card View */}
      <div className="md:hidden">
        {venuesWithDestination.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p className="text-sm">No venues found</p>
          </div>
        ) : (
          <VStack gap={3}>
            {venuesWithDestination.map((item) => {
              const displayName = item.destination.destination_type === 'region'
                ? (item.destination.display_name || item.destination.city)
                : item.destination.city === item.destination.country
                  ? item.destination.country
                  : `${item.destination.city}, ${item.destination.country}`;
              
              const isConnected = !!item.shortlistItem.cvent_supplier_id;
              const hasSubmittedProposal = item.shortlistItem.cvent_proposal_status === 'SUBMITTED_PROPOSAL';
              const isDeclinedProposal = isDeclinedProposalStatus(item.shortlistItem.cvent_proposal_status);
              const cventId = item.shortlistItem.cvent_supplier_id;
              const cventVenueName = cventId && cventVenueMap?.get(cventId);
              const tooltipContent = cventVenueName 
                ? (
                  <div className="text-left">
                    <div>{cventVenueName}</div>
                    <div className="text-xs text-gray-400 mt-0.5">Cvent ID: {cventId}</div>
                  </div>
                )
                : cventId 
                  ? `Cvent ID: ${cventId}`
                  : 'Not connected to Cvent';
              
              return (
                <div key={`${item.destination.id}-${item.venue.id}`}>
                  <VenueCard
                    venue={item.venue}
                    isShortlisted={true}
                    onViewDetails={() => onViewDetails(item.venue)}
                    showAIScores={true}
                  />
                  {onUpdateNotes && (
                    <div className="mt-2 px-3 flex items-center justify-end">
                      <NoteIcon
                        notes={item.shortlistItem.notes}
                        clientFacing={item.shortlistItem.notes_client_facing}
                        venueName={item.venue.property_name}
                        onUpdateNotes={(notes, clientFacing) => onUpdateNotes(item.shortlistItem.id, notes, clientFacing)}
                      />
                    </div>
                  )}
                  <div className="mt-2 px-3 flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">
                      Destination: {displayName}
                    </span>
                    <div className="flex items-center gap-1.5">
                      {isConnected ? (
                        <>
                          <SimpleTooltip
                            content={tooltipContent}
                            side="top"
                          >
                            <Badge variant="success" className="text-xs px-1.5 py-0">
                              Cvent
                            </Badge>
                          </SimpleTooltip>
                          {eventId && (
                            <SimpleTooltip
                              content="Disconnect from Cvent"
                              side="top"
                            >
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDisconnectVenue(item.shortlistItem.id, item.venue.property_name);
                                }}
                                disabled={disconnectingIds.has(item.shortlistItem.id)}
                                className={`
                                  p-0.5 rounded hover:bg-error-50 transition-colors
                                  ${disconnectingIds.has(item.shortlistItem.id) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                                `}
                                aria-label="Disconnect from Cvent"
                              >
                                <X className="w-3 h-3 text-error-600" />
                              </button>
                            </SimpleTooltip>
                          )}
                        </>
                      ) : (
                        <>
                          <SimpleTooltip
                            content="Not connected to Cvent"
                            side="top"
                          >
                            <Badge variant="secondary" className="text-xs px-1.5 py-0">
                              No Cvent
                            </Badge>
                          </SimpleTooltip>
                          <SimpleTooltip content="Remove from shortlist" side="top">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveFromShortlist(item.shortlistItem.id, item.venue.property_name);
                              }}
                              disabled={removingIds.has(item.shortlistItem.id)}
                              className={`
                                p-0.5 rounded hover:bg-error-50 transition-colors
                                ${removingIds.has(item.shortlistItem.id) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                              `}
                              aria-label="Remove from shortlist"
                            >
                              <Trash2 className="w-3 h-3 text-error-600" />
                            </button>
                          </SimpleTooltip>
                        </>
                      )}
                    </div>
                  </div>
                  {cventRfpId && (
                    <div className="mt-2 px-3 flex flex-wrap items-center justify-end gap-2">
                      {item.shortlistItem.cvent_proposal_status && (
                        <div className="mr-auto">
                          {getStatusBadge(item.shortlistItem.cvent_proposal_status, !!item.shortlistItem.is_manual_proposal)}
                        </div>
                      )}
                      {!hasSubmittedProposal && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setManualProposalModal({
                            shortlistId: item.shortlistItem.id,
                            venueName: item.venue.property_name,
                            mode: 'received',
                            initialData: null,
                          })}
                          leftIcon={<FileCheck className="w-3.5 h-3.5" />}
                        >
                          Mark Received
                        </Button>
                      )}
                      {!hasSubmittedProposal && !isDeclinedProposal && (
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => setManualProposalModal({
                            shortlistId: item.shortlistItem.id,
                            venueName: item.venue.property_name,
                            mode: 'declined',
                            initialData: null,
                          })}
                          leftIcon={<Ban className="w-3.5 h-3.5" />}
                        >
                          Mark Declined
                        </Button>
                      )}
                      {item.shortlistItem.is_manual_proposal && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleUnmarkManualProposal(item.shortlistItem.id, item.venue.property_name)}
                          disabled={unmarkingIds.has(item.shortlistItem.id)}
                          leftIcon={<Unlink className="w-3.5 h-3.5" />}
                        >
                          Remove Manual
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </VStack>
        )}
      </div>
      
      {/* Desktop: Table View */}
      <div className="hidden md:block">
        <Table
          data={venuesWithDestination}
          columns={columns}
          loading={false}
          searchable={false}
          pagination={false}
          emptyText="No venues found"
        />
      </div>

      {/* Proposal Details Modal */}
      {selectedProposal && (
        <ProposalDetailsModal
          isOpen={!!selectedProposal}
          onClose={() => setSelectedProposal(null)}
          rfpId={selectedProposal.rfpId}
          supplierId={selectedProposal.supplierId}
          venueName={selectedProposal.venueName}
          shortlistId={selectedProposal.shortlistId}
        />
      )}

      {/* Manual Proposal Modal */}
      <ManualProposalModal
        isOpen={!!manualProposalModal}
        onClose={() => setManualProposalModal(null)}
        onSubmit={handleMarkManualProposal}
        onDecline={handleMarkManualDeclined}
        venueName={manualProposalModal?.venueName || ''}
        isSubmitting={isSubmittingManualProposal}
        isDeclining={isDecliningManualProposal}
        mode={manualProposalModal?.mode || 'received'}
        initialData={manualProposalModal?.initialData}
      />

      <ConfirmDialog
        isOpen={!!disconnectConfirm}
        onClose={() => setDisconnectConfirm(null)}
        onConfirm={handleConfirmDisconnect}
        title="Disconnect from Cvent"
        description={`This will disconnect ${disconnectConfirm?.venueName || 'this venue'} from Cvent and clear all proposal data (rates, costs, PDF, etc.). You can reconnect later to re-import the data.`}
        confirmText="Disconnect & Clear Data"
        variant="destructive"
      />

      {/* Manual Cvent connect modal */}
      {manualConnectFor && eventId && cventSuppliers && (
        <CventManualConnectModal
          isOpen={!!manualConnectFor}
          onClose={() => setManualConnectFor(null)}
          eventId={eventId}
          shortlistId={manualConnectFor.shortlistId}
          shortlistVenueName={manualConnectFor.venueName}
          cventSuppliers={cventSuppliers}
          alreadyConnectedTo={supplierToConnectedShortlistName}
          onConnected={() => {
            onVenueManuallyConnected?.();
          }}
        />
      )}
    </div>
  );
}
